/**
 * Utility functions for formatting and normalizing data.
 *
 * This is the one home for the backoffice's date, duration and identifier
 * formatters. Add a helper here rather than re-declaring it in a component:
 * the timeline surfaces render the same clock and the same visitor id, so a
 * local copy means the next change to how a reading displays has to be made
 * in several places at once, with nothing keeping them in step.
 */

import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';

/**
 * Formats duration in seconds to human-readable string (e.g., "5m 30s", "2h 15m")
 */
export function formatDuration(seconds: number | null): string {
    if (seconds === null || seconds === undefined) {
        return '-';
    }
    if (seconds < 60) {
        return `${Math.round(seconds)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes < 60) {
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/**
 * Formats a date string to localized date format (e.g., "16/01/2026")
 */
export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

/**
 * Formats a date string as a UTC calendar date (e.g., "Sep 28, 2026").
 *
 * Used for billing period boundaries, which are UTC instants: rendering them
 * in local time would shift the day for a viewer west of UTC.
 */
export function formatUtcDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
}

const SHORT_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

/**
 * Formats a date as an English month abbreviation and day of month in local
 * time (e.g., "Nov 25"), independent of the browser locale.
 */
export function formatShortDate(date: Date): string {
    return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Formats a date string to a time of day on the 24-hour clock (e.g., "14:30").
 *
 * Used by the timeline surfaces, where the day is already carried by the date
 * separator and only the clock time belongs on the row.
 */
export function formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Formats a date string to localized datetime format (e.g., "16/01/2026, 14:30")
 */
export function formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Formats a date string to human-readable relative time.
 * Examples: "3 min ago", "Today at 14:04", "Yesterday at 10:30", "3 days ago"
 */
export function formatRelativeDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const timeStr = format(date, 'HH:mm');

    if (isToday(date)) {
        return `Today at ${timeStr}`;
    }

    if (isYesterday(date)) {
        return `Yesterday at ${timeStr}`;
    }

    // For older dates, use formatDistanceToNow (e.g., "3 days ago", "about 1 month ago")
    return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Shortens a visitor id to the prefix the tables show, or "-" when there is none.
 */
export function formatVisitorId(visitorId: string | null): string {
    if (!visitorId) return '-';
    return visitorId.slice(0, 8);
}

/**
 * Normalizes a URL for deduplication by extracting just the pathname.
 * Removes query params, hash, and trailing slashes.
 */
export function normalizePageUrl(url: string): string {
    try {
        const urlObj = new URL(url);
        let pathname = urlObj.pathname;
        // Remove trailing slash (except for root)
        if (pathname.length > 1 && pathname.endsWith('/')) {
            pathname = pathname.slice(0, -1);
        }
        // Return root as "/" to deduplicate all home pages
        return pathname || '/';
    } catch {
        return url;
    }
}

/**
 * Extracts a human-readable page name from a URL or pathname.
 * e.g., "/" -> "Home", "/pricing" -> "Pricing", "/about-us" -> "About Us"
 */
/**
 * Prettifies user-submitted text for display.
 * Trims whitespace, converts ALL CAPS to sentence case, and capitalizes the first letter.
 */
export function prettifyText(text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return trimmed;

    // All-caps → sentence case
    const letters = trimmed.replace(/[^a-zA-Z]/g, '');
    if (letters.length > 1 && letters === letters.toUpperCase()) {
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    }

    // Capitalize first letter
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function extractPageName(pathOrUrl: string): string {
    // Handle pathname (e.g., "/" or "/pricing") or full URL
    const pathname = pathOrUrl.startsWith('http')
        ? (() => {
              try {
                  return new URL(pathOrUrl).pathname;
              } catch {
                  return pathOrUrl;
              }
          })()
        : pathOrUrl;

    if (pathname === '/' || pathname === '') {
        return 'Home';
    }
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
        return 'Home';
    }
    const lastSegment = segments[segments.length - 1];
    return lastSegment
        .replace(/[-_]/g, ' ')
        .replace(/\.(html?|php|aspx?)$/i, '')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
